import { API_BASE, getToken } from "./api.js";

/**
 * The live connection to the diver's cohort chats (backend/app/chats/socket.py).
 * One connection for a page, watching as many chats as it subscribes to.
 *
 * Sends the session token as its first message (never in the URL, which ends
 * up in server logs), then subscribes to each chat. Callbacks:
 * - onEvent(event): message.created / edited / deleted, read, typing, each
 *   with its chat_id.
 * - onSubscribed(chatId, { again }): the chat is being watched; `again` when
 *   it was watched before a dropped connection, so what was missed should be
 *   fetched now.
 * - onChatClosed(chatId, { code, error }): the server shut that chat to us (no
 *   longer a member, say). The others carry on.
 * - onStatus(status): "connecting", "live", "reconnecting", "refused" (signed
 *   out: retrying can't help) or "unavailable" (no WebSocket here).
 *
 * A dropped connection is retried after RETRY_MS, longer each time, and every
 * chat is subscribed to again.
 */
export const RETRY_MS = [1000, 2000, 5000, 10000, 30000];

/** Codes that end one chat's subscription. */
const CHAT_ENDED = new Set(["chat_members_only", "not_found"]);

export function socketUrl(base = API_BASE) {
  const url = new URL(`${base}/chat/socket`);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

export function openChatSocket({
  onEvent = () => {},
  onSubscribed = () => {},
  onChatClosed = () => {},
  onStatus = () => {},
} = {}) {
  const WebSocketImpl = globalThis.WebSocket;
  /** Chats to watch, and those watched on an earlier connection. */
  const wanted = new Set();
  const watchedBefore = new Set();
  let ws = null;
  let signedIn = false;
  let attempt = 0;
  let stopped = false;
  let retryTimer = null;

  const send = (message) => {
    if (ws && ws.readyState === 1) ws.send(JSON.stringify(message));
  };

  function hangUp() {
    clearTimeout(retryTimer);
    try {
      ws?.close();
    } catch {
      // Already closing: nothing to do.
    }
  }

  function connect() {
    if (typeof WebSocketImpl !== "function") {
      onStatus("unavailable");
      return;
    }
    signedIn = false;
    onStatus(attempt ? "reconnecting" : "connecting");
    ws = new WebSocketImpl(socketUrl());
    ws.onopen = () => send({ type: "auth", token: getToken() });
    ws.onmessage = (event) => {
      let message;
      try {
        message = JSON.parse(event.data);
      } catch {
        return; // Not ours to understand.
      }
      if (message.type === "ready") {
        signedIn = true;
        attempt = 0;
        onStatus("live");
        for (const chatId of wanted) send({ type: "subscribe", chat_id: chatId });
      } else if (message.type === "subscribed") {
        const again = watchedBefore.has(message.chat_id);
        watchedBefore.add(message.chat_id);
        onSubscribed(message.chat_id, { again });
      } else if (message.type === "closed") {
        wanted.delete(message.chat_id);
        onChatClosed(message.chat_id, message);
      } else if (message.type === "error") {
        if (message.code === "unauthorized") {
          stopped = true;
          hangUp();
          onStatus("refused", message);
        } else if (CHAT_ENDED.has(message.code) && message.chat_id != null) {
          wanted.delete(message.chat_id);
          onChatClosed(message.chat_id, message);
        }
        // Anything else (a typing event too early, say) changes nothing.
      } else if (message.type !== "pong") {
        onEvent(message);
      }
    };
    ws.onclose = () => {
      signedIn = false;
      if (stopped) return;
      const delay = RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)];
      attempt += 1;
      onStatus("reconnecting");
      retryTimer = setTimeout(connect, delay);
    };
  }

  connect();

  return {
    subscribe(chatId) {
      if (wanted.has(chatId)) return;
      wanted.add(chatId);
      if (signedIn) send({ type: "subscribe", chat_id: chatId });
    },
    unsubscribe(chatId) {
      wanted.delete(chatId);
      watchedBefore.delete(chatId);
      if (signedIn) send({ type: "unsubscribe", chat_id: chatId });
    },
    /** Tell a chat's members this diver is typing. The server passes it on at
     *  most every couple of seconds, so calling it on every keystroke is fine. */
    typing(chatId) {
      if (signedIn && wanted.has(chatId)) send({ type: "typing", chat_id: chatId });
    },
    /** Hang up for good: leaving the page. */
    close() {
      stopped = true;
      hangUp();
    },
  };
}
